import React, { useState } from "react";
import { ArrowRight, Check, CreditCard, Flame, Gift, ShieldCheck, Sparkles, X } from "lucide-react";
import { apiUrl } from "../utils/api";

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type PlanId =
  | "observer_monthly"
  | "observer_standard"
  | "operator_quantitative"
  | "strategist_tactical"
  | "advisory_executive"
  | "enterprise_sovereign"
  | "deep_sky_reading"
  | "solar_fuel_pack_50";

type PlanOption = {
  id: PlanId;
  name: string;
  price: string;
  cadence: string;
  summary: string;
  features: string[];
  fuel: string;
  badge?: string;
  featured?: boolean;
};

const PLAN_OPTIONS: PlanOption[] = [
  {
    id: "observer_monthly",
    name: "Observer",
    price: "$9",
    cadence: "/ month",
    summary: "A calm first step into a persistent personal observatory.",
    features: ["Full spatial Observatory access", "Personal transit tracking", "500 Solar Fuel units monthly"],
    fuel: "500 fuel",
  },
  {
    id: "operator_quantitative",
    name: "Operator",
    price: "$17",
    cadence: "/ month",
    summary: "The balanced choice for people who use timing and comparison every week.",
    features: ["Everything in Observer", "Astro-Chronos time series", "Five private persona profiles", "1,200 Solar Fuel units monthly"],
    fuel: "1,200 fuel",
    badge: "Most chosen",
    featured: true,
  },
  {
    id: "strategist_tactical",
    name: "Strategist",
    price: "$36",
    cadence: "/ month",
    summary: "For high-stakes decisions that need deeper analysis and repeatable protocols.",
    features: ["Everything in Operator", "Eye in the Sky readings", "Draconic coordinate frames", "Fifteen private persona profiles", "3,000 Solar Fuel units monthly"],
    fuel: "3,000 fuel",
    badge: "Best value for active users",
  },
  {
    id: "advisory_executive",
    name: "Advisory",
    price: "$71",
    cadence: "/ month",
    summary: "A higher-touch workspace for advisors, partners, and multi-entity analysis.",
    features: ["Everything in Strategist", "Custom decision frameworks", "Corporate entity projection", "7,500 Solar Fuel units monthly"],
    fuel: "7,500 fuel",
  },
  {
    id: "enterprise_sovereign",
    name: "Enterprise",
    price: "$141",
    cadence: "/ month",
    summary: "Expanded capacity for teams running the Observatory as an operating system.",
    features: ["Everything in Advisory", "Unlimited private entities", "Priority workload capacity", "20,000 Solar Fuel units monthly"],
    fuel: "20,000 fuel",
  },
  {
    id: "deep_sky_reading",
    name: "Deep Sky Pass",
    price: "$49",
    cadence: "one-time",
    summary: "A focused analysis without a recurring membership commitment.",
    features: ["One comprehensive reading", "150 Solar Fuel units", "Verified timing corridors"],
    fuel: "150 fuel",
  },
  {
    id: "solar_fuel_pack_50",
    name: "Solar Fuel Pack",
    price: "$25",
    cadence: "one-time",
    summary: "Add a measured reserve of protocol capacity without changing your membership.",
    features: ["50 Solar Fuel units", "Use across eligible protocols", "No recurring charge"],
    fuel: "50 fuel",
  },
];

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("operator_quantitative");

  if (!isOpen) return null;

  const selected = PLAN_OPTIONS.find((plan) => plan.id === selectedPlan) ?? PLAN_OPTIONS[1];

  const startCheckout = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage("");
    try {
      const referralCode = window.localStorage.getItem("nex_referral_code") || new URLSearchParams(window.location.search).get("ref") || undefined;
      const response = await fetch(apiUrl("/api/stripe/create-checkout-session"), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-NEX-Referral-Code": referralCode ?? "" },
        credentials: "include",
        body: JSON.stringify({
          plan: selectedPlan,
          customer_email: email.trim() || null,
          user_reference: referralCode ?? null,
        }),
      });
      const payload = await response.json() as { checkout_url?: string; detail?: string };
      if (!response.ok || !payload.checkout_url) {
        setMessage(payload.detail ?? "Checkout could not be established.");
        return;
      }
      window.location.assign(payload.checkout_url);
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : "Checkout initialization failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const referralParam = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("ref") : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-md">
      <div className="glass-panel flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-cyan-400/35 bg-slate-950/95 shadow-2xl" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
        <header className="flex items-start justify-between gap-4 border-b border-slate-800 p-6">
          <div>
            <div className="flex items-center gap-2 text-cyan-300"><Sparkles className="h-5 w-5" /><span className="text-lg font-bold" style={{ fontVariant: "small-caps" }}>Observatory access</span></div>
            <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-400">Choose the amount of depth you will actually use. Every paid tier is month-to-month, clearly priced, and cancellable through the billing portal.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close pricing" className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
        </header>
        <div className="overflow-y-auto p-6">
          {referralParam && <div className="mb-5 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200"><Gift className="h-4 w-4 text-emerald-300" /> Referral attribution is active for this checkout.</div>}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {PLAN_OPTIONS.map((plan) => {
              const active = selectedPlan === plan.id;
              return <button key={plan.id} type="button" onClick={() => setSelectedPlan(plan.id)} className={active ? "relative rounded-2xl border border-cyan-300 bg-cyan-950/45 p-4 text-left shadow-[0_0_30px_rgba(34,211,238,0.16)]" : "relative rounded-2xl border border-slate-800 bg-slate-900/55 p-4 text-left transition hover:border-slate-600"}>
                {plan.badge && <span className={plan.featured ? "absolute -top-2 right-3 rounded-full bg-cyan-300 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-slate-950" : "absolute -top-2 right-3 rounded-full border border-amber-300/40 bg-amber-950 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-amber-200"}>{plan.badge}</span>}
                <div className="flex items-start justify-between gap-3"><div><h3 className="text-base font-bold text-white">{plan.name}</h3><p className="mt-1 text-[11px] leading-5 text-slate-400">{plan.summary}</p></div><div className="shrink-0 text-right"><strong className="text-xl text-cyan-200">{plan.price}</strong><span className="block text-[10px] text-slate-500">{plan.cadence}</span></div></div>
                <ul className="mt-4 space-y-2 text-[11px] text-slate-300">{plan.features.map((feature) => <li key={feature} className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" />{feature}</li>)}</ul>
                <span className="mt-4 flex items-center gap-1.5 border-t border-slate-800 pt-3 text-[10px] uppercase tracking-wider text-amber-200"><Flame className="h-3.5 w-3.5" />{plan.fuel}</span>
              </button>;
            })}
          </div>
          <form onSubmit={startCheckout} className="mt-6 grid gap-4 rounded-2xl border border-cyan-400/20 bg-slate-900/60 p-5 md:grid-cols-[1fr_auto] md:items-end">
            <label className="block text-xs text-slate-300">Receipt email <span className="text-slate-500">optional when already signed in</span><input value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="founder@venture.com" className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-xs text-white outline-none focus:border-cyan-400" /></label>
            <button type="submit" disabled={isLoading} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-50"><CreditCard className="h-4 w-4" />{isLoading ? "CONNECTING TO STRIPE" : `Continue with ${selected.name}`}<ArrowRight className="h-4 w-4" /></button>
            {message && <p className="text-xs text-rose-300 md:col-span-2">{message}</p>}
          </form>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-[10px] leading-4 text-slate-500"><span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Secure Stripe checkout</span><span>Cancel any time</span><span>Upgrade or downgrade without losing account history</span></div>
        </div>
      </div>
    </div>
  );
};
